import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, Button, Select, message, Card, Space, Row, Col, Typography, Spin, Divider, Checkbox, Tooltip, Collapse, Tag, Alert, Tabs } from 'antd';
import { SaveOutlined, ArrowLeftOutlined, SettingOutlined, InfoCircleOutlined, PlusOutlined, MinusCircleOutlined, MenuOutlined, EyeOutlined, EyeInvisibleOutlined, CodeOutlined } from '@ant-design/icons';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../../services/api';
import MetricCodeSelector from '../../components/MetricCodeSelector';
import BrandCodes from '../../components/BrandCodes';

const { Title } = Typography;
const { Panel } = Collapse;
const { TabPane } = Tabs;

const BrandEditPage = () => {
  const { id: brandId } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(!!brandId);
  const [submitting, setSubmitting] = useState(false);
  const [brandData, setBrandData] = useState(null);
  const [metrics, setMetrics] = useState([]); // All available active metrics
  const [selectedMetrics, setSelectedMetrics] = useState([]); // Metrics currently associated with the brand
  const [metricDetails, setMetricDetails] = useState({}); // Stores code, variables, visibility, order for selected metrics
  const [metricCodeList, setMetricCodeList] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [advisorsLoading, setAdvisorsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('info'); // Default tab is 'info'

  const isNewBrand = !brandId;

  // Fetch Advisors
  useEffect(() => {
    const fetchAdvisors = async () => {
      try {
        setAdvisorsLoading(true);
        const response = await api.get('/advisors');
        setAdvisors(response.data || []);
      } catch (error) {
        console.error('Advisor listesi yüklenirken hata:', error);
        message.error('Advisor listesi yüklenemedi');
        setAdvisors([]);
      } finally {
        setAdvisorsLoading(false);
      }
    };
    fetchAdvisors();
  }, []);

  // Fetch All Active Metrics & Metric Codes
  useEffect(() => {
    const fetchRequiredData = async () => {
      try {
        setLoading(true);
        const [metricsResponse, codesResponse] = await Promise.all([
          api.get('/metrics', { params: { scope: 'all' } }),
          api.get('/metriccodes')
        ]);

        if (metricsResponse.data && Array.isArray(metricsResponse.data)) {
            const activeMetrics = metricsResponse.data.filter(metric => !metric.isDeleted);
            setMetrics(activeMetrics);
        } else {
             console.error('Beklenmeyen metrik API yanıtı:', metricsResponse.data);
             message.error('Metrikler yüklenirken bir hata oluştu');
        }

         if (codesResponse.data && Array.isArray(codesResponse.data?.data)) {
             setMetricCodeList(codesResponse.data.data);
         } else {
             console.error('Beklenmeyen metrik kod API yanıtı:', codesResponse.data);
             setMetricCodeList([]);
             message.error('Metrik kodları yüklenirken hata oluştu.');
         }

      } catch (error) {
        console.error('Gerekli veriler yüklenirken hata:', error);
        message.error('Sayfa verileri yüklenirken bir hata oluştu.');
      } finally {
         // Keep loading true if editing an existing brand, data will be fetched next
         if (isNewBrand) {
            setLoading(false);
         }
      }
    };
    fetchRequiredData();
  }, [isNewBrand]);

  // Fetch Brand Details if Editing
  useEffect(() => {
    if (brandId) {
      const fetchBrandDetails = async () => {
        setLoading(true);
        try {
          // Fetch brand with its associated metrics
          const response = await api.get(`/brands/${brandId}`, { params: { includeMetrics: true } });
          const data = response.data;
          setBrandData(data);
          form.setFieldsValue({
            name: data.name,
            currency: data.currency || 'TRY',
            template: data.template || 'WEB AUTOBUDGET',
            advisor: data.advisor || '',
            growth: data.growth || '',
            assistant: data.assistant || '',
            meta: data.meta || '',
            google: data.google || '',
            seo: data.seo || ''
          });

          // Process associated metrics
          const associatedMetrics = (data.Metrics || [])
            .filter(m => m.BrandMetric?.isActive && !m.isDeleted) // Filter active and non-deleted metrics
            .sort((a, b) => (a.BrandMetric?.displayOrder || 0) - (b.BrandMetric?.displayOrder || 0)); // Sort by displayOrder

          setSelectedMetrics(associatedMetrics);

          const details = {};
          associatedMetrics.forEach((metric, index) => {
            details[metric.id] = {
              metricId: metric.id,
              displayOrder: metric.BrandMetric?.displayOrder ?? index + 1,
              code: metric.BrandMetric?.code || null,
              variables: metric.BrandMetric?.variables || null,
              isVisible: metric.BrandMetric?.isVisible !== false // Default to true if undefined
            };
          });
          setMetricDetails(details);

        } catch (error) {
          console.error('Marka detayları yüklenirken hata:', error);
          message.error('Marka detayları yüklenemedi. Marka listesine yönlendiriliyorsunuz.');
          navigate('/brands');
        } finally {
          setLoading(false);
        }
      };
      fetchBrandDetails();
    }
  }, [brandId, navigate, form]);


  const handleFormSubmit = async (values) => {
    setSubmitting(true);
    try {
        const payload = { ...values };
        let response;
        if (isNewBrand) {
            response = await api.post('/brands', payload);
            message.success('Marka başarıyla oluşturuldu');
            // Redirect to the edit page of the newly created brand to add metrics
            navigate(`/brands/${response.data.id}/edit`);
        } else {
            response = await api.put(`/brands/${brandId}`, payload);
            message.success('Marka başarıyla güncellendi');
            // Optionally refetch data or just show success
             setBrandData(prev => ({ ...prev, ...payload })); // Optimistic update
        }

    } catch (error) {
         console.error('Marka kaydetme hatası:', error.response || error);
         message.error(error.response?.data?.error || 'Marka kaydedilirken bir hata oluştu');
    } finally {
        setSubmitting(false);
    }
  };

 const handleMetricSettingsSubmit = async () => {
    setSubmitting(true);
    try {
      if (!brandId) {
        message.error('Metrik ayarları için marka ID gereklidir.');
        return;
      }

      // Ensure metrics are ordered correctly based on the state `selectedMetrics`
       const orderedPayloadMetrics = selectedMetrics.map((metric, index) => {
            const details = metricDetails[metric.id] || {};
            return {
                metricId: parseInt(metric.id),
                displayOrder: index + 1, // Use index from the ordered selectedMetrics array
                code: details.code || null,
                variables: details.variables || null,
                isVisible: details.isVisible !== false // Default to true
            };
       });


      console.log('Gönderilen metrik ayarları:', { metrics: orderedPayloadMetrics });

      const response = await api.put(`/brands/${brandId}/metrics`, {
        metrics: orderedPayloadMetrics
      });

      if (response.data?.success !== false) { // Allow success if no explicit error
        message.success('Metrik ayarları başarıyla güncellendi');
        // Optionally refetch brand data to confirm changes
        // fetchBrandDetails(); // Uncomment if needed
      } else {
         throw new Error(response.data?.error || 'API tarafından bilinmeyen bir hata döndü');
      }

    } catch (error) {
      console.error('Metrik ayarları güncelleme hatası:', error);
      message.error(error.response?.data?.error || error.message || 'Metrik ayarları güncellenirken bir hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };


  const onMetricsDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const newSelectedMetrics = Array.from(selectedMetrics);
    const [removed] = newSelectedMetrics.splice(source.index, 1);
    newSelectedMetrics.splice(destination.index, 0, removed);
    setSelectedMetrics(newSelectedMetrics);

    // Update displayOrder in metricDetails immediately for consistency (optional)
    // const updatedDetails = { ...metricDetails };
    // newSelectedMetrics.forEach((metric, index) => {
    //   if (updatedDetails[metric.id]) {
    //     updatedDetails[metric.id].displayOrder = index + 1;
    //   }
    // });
    // setMetricDetails(updatedDetails);
  };

  const handleAddMetric = (metricId) => {
      const metricToAdd = metrics.find(m => m.id === metricId);
      if (metricToAdd && !selectedMetrics.some(sm => sm.id === metricId)) {
          setSelectedMetrics(prev => [...prev, metricToAdd]);
          // Initialize details for the new metric
          setMetricDetails(prev => ({
                ...prev,
                [metricId]: {
                    metricId: metricId,
                    displayOrder: selectedMetrics.length + 1, // Temp order
                    code: null,
                    variables: null,
                    isVisible: true
                }
            }));
      }
  };

  const handleRemoveMetric = useCallback((metricIdToRemove) => {
    setSelectedMetrics(prev => prev.filter(metric => metric.id !== metricIdToRemove));
    // Remove details as well
    setMetricDetails(prev => {
        const newDetails = { ...prev };
        delete newDetails[metricIdToRemove];
        return newDetails;
    });
  }, []);

   const handleMetricDetailChange = useCallback((metricId, field, value) => {
        setMetricDetails(prev => ({
            ...prev,
            [metricId]: {
                ...(prev[metricId] || { metricId: metricId, isVisible: true }), // Ensure object exists
                [field]: value
            }
        }));
    }, []);

    // Handle changes from MetricCodeSelector
    const handleMetricCodeSelectionChange = useCallback((metricId, codeData) => {
        handleMetricDetailChange(metricId, 'code', codeData ? codeData.code : null);
        handleMetricDetailChange(metricId, 'variables', codeData ? codeData.variables : null);
    }, [handleMetricDetailChange]);

    const handleMetricVisibilityChange = useCallback((metricId, isVisible) => {
        handleMetricDetailChange(metricId, 'isVisible', isVisible);
    }, [handleMetricDetailChange]);


  // Available metrics for selection (excluding already selected ones)
  const availableMetricsForSelection = metrics.filter(
    m => !selectedMetrics.some(sm => sm.id === m.id)
  );

  // Tab değiştiğinde çağrılır
  const handleTabChange = (key) => {
    setActiveTab(key);
  };

  if (loading) {
    return <Spin tip="Marka verileri yükleniyor..." style={{ display: 'block', marginTop: '50px' }} />;
  }

  // Render the brand information tab content
  const renderBrandInfoTab = () => (
    <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
      <Card title="Marka Bilgileri" bordered={false}>
        <Form.Item
          name="name"
          label="Marka Adı"
          rules={[{ required: true, message: 'Lütfen marka adını girin' }, { max: 100 }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="currency" label="Para Birimi" initialValue="TRY">
          <Select>
            <Select.Option value="TRY">TRY</Select.Option>
            <Select.Option value="USD">USD</Select.Option>
            <Select.Option value="EUR">EUR</Select.Option>
            <Select.Option value="GBP">GBP</Select.Option>
            <Select.Option value="CAD">CAD</Select.Option>
            <Select.Option value="AUD">AUD</Select.Option>
            <Select.Option value="CHF">CHF</Select.Option>
            <Select.Option value="JPY">JPY</Select.Option>
            <Select.Option value="CNY">CNY</Select.Option>
            <Select.Option value="RUB">RUB</Select.Option>
            <Select.Option value="INR">INR</Select.Option>
            <Select.Option value="RON">RON</Select.Option>
            <Select.Option value="BGN">BGN</Select.Option>
            <Select.Option value="BRL">BRL</Select.Option>
            <Select.Option value="HUF">HUF</Select.Option>
            <Select.Option value="PLN">PLN</Select.Option>
            <Select.Option value="SEK">SEK</Select.Option>
            <Select.Option value="NOK">NOK</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name="template" label="Template" initialValue="WEB AUTOBUDGET">
          <Select>
            <Select.Option value="WEB AUTOBUDGET">WEB AUTOBUDGET</Select.Option>
            <Select.Option value="WEB MANUELBUDGET">WEB MANUELBUDGET</Select.Option>
            <Select.Option value="TRENDYOL">TRENDYOL</Select.Option>
          </Select>
        </Form.Item>
        <Form.Item name="advisor" label="Danışman">
          <Select loading={advisorsLoading} allowClear>
            <Select.Option value="">-- Seçiniz --</Select.Option>
            {advisors.map(advisor => (
                <Select.Option key={advisor.id || advisor.advisor_name} value={advisor.advisor_name}>
                    {advisor.advisor_name}
                </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="growth" label="Growth">
          <Select loading={advisorsLoading} allowClear>
            <Select.Option value="">-- Seçiniz --</Select.Option>
            {advisors.map(advisor => (
                <Select.Option key={`growth_${advisor.id || advisor.advisor_name}`} value={advisor.advisor_name}>
                    {advisor.advisor_name}
                </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="assistant" label="Assistant">
          <Select loading={advisorsLoading} allowClear>
            <Select.Option value="">-- Seçiniz --</Select.Option>
            {advisors.map(advisor => (
                <Select.Option key={`assistant_${advisor.id || advisor.advisor_name}`} value={advisor.advisor_name}>
                    {advisor.advisor_name}
                </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="meta" label="Meta/Facebook">
          <Select loading={advisorsLoading} allowClear>
            <Select.Option value="">-- Seçiniz --</Select.Option>
            {advisors.map(advisor => (
                <Select.Option key={`meta_${advisor.id || advisor.advisor_name}`} value={advisor.advisor_name}>
                    {advisor.advisor_name}
                </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="google" label="Google Ads">
          <Select loading={advisorsLoading} allowClear>
            <Select.Option value="">-- Seçiniz --</Select.Option>
            {advisors.map(advisor => (
                <Select.Option key={`google_${advisor.id || advisor.advisor_name}`} value={advisor.advisor_name}>
                    {advisor.advisor_name}
                </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item name="seo" label="SEO">
          <Select loading={advisorsLoading} allowClear>
            <Select.Option value="">-- Seçiniz --</Select.Option>
            {advisors.map(advisor => (
                <Select.Option key={`seo_${advisor.id || advisor.advisor_name}`} value={advisor.advisor_name}>
                    {advisor.advisor_name}
                </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting} icon={<SaveOutlined />}>
            Marka Bilgilerini Kaydet
          </Button>
        </Form.Item>
      </Card>
    </Form>
  );

  // Render the metrics tab content
  const renderMetricsTab = () => (
    <Card title="Metrik Ayarları" bordered={false}>
      <Alert
          message="Metrik Ekleme ve Sıralama"
          description="Aşağıdaki listeden markaya metrik ekleyebilir, eklenen metrikleri sürükleyip bırakarak sıralayabilir ve her metrik için özel ayarları (kod, görünürlük vb.) yapabilirsiniz."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
      />

      <Form.Item label="Metrik Ekle">
        <Select
            showSearch
            placeholder="Eklemek için bir metrik seçin"
            optionFilterProp="children"
            onChange={handleAddMetric}
            value={null} // Reset selection after adding
            filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
            }
        >
            {availableMetricsForSelection.map(metric => (
                <Select.Option key={metric.id} value={metric.id}>
                    {metric.name}
                </Select.Option>
            ))}
        </Select>
      </Form.Item>

      <Divider>Seçili Metrikler</Divider>

      {selectedMetrics.length === 0 ? (
          <p>Bu marka için henüz metrik seçilmedi.</p>
      ) : (
        <DragDropContext onDragEnd={onMetricsDragEnd}>
            <Droppable droppableId="selected-metrics">
            {(provided) => (
                <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={{ minHeight: '100px', background: '#fafafa', padding: '8px', borderRadius: '4px' }}
                >
                {selectedMetrics.map((metric, index) => (
                    <Draggable key={metric.id} draggableId={String(metric.id)} index={index}>
                    {(provided, snapshot) => (
                        <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                            ...provided.draggableProps.style,
                            userSelect: 'none',
                            padding: '12px',
                            marginBottom: '8px',
                            background: snapshot.isDragging ? '#e6f7ff' : 'white',
                            border: '1px solid #f0f0f0',
                            borderRadius: '4px',
                        }}
                        >
                        <Row gutter={16} align="middle">
                            <Col {...provided.dragHandleProps} style={{ cursor: 'grab' }}>
                                <MenuOutlined />
                            </Col>
                            <Col flex="auto">
                                <Typography.Text strong>{metric.name}</Typography.Text> ({metric.dataType})
                                <Collapse ghost>
                                    <Panel header="Detaylar & Kod Seçimi" key={metric.id} >
                                        <MetricCodeSelector
                                            metricId={metric.id}
                                            metricCodeList={metricCodeList}
                                            value={metricDetails[metric.id]?.code}
                                            variables={metricDetails[metric.id]?.variables}
                                            onChange={handleMetricCodeSelectionChange}
                                        />
                                    </Panel>
                                </Collapse>
                            </Col>
                            <Col>
                                <Space>
                                    <Tooltip title={metricDetails[metric.id]?.isVisible !== false ? "Metriği Gizle" : "Metriği Göster"}>
                                        <Button
                                            icon={metricDetails[metric.id]?.isVisible !== false ? <EyeOutlined /> : <EyeInvisibleOutlined />}
                                            onClick={() => handleMetricVisibilityChange(metric.id, !(metricDetails[metric.id]?.isVisible !== false))}
                                            type={metricDetails[metric.id]?.isVisible !== false ? "default" : "dashed"}
                                        />
                                    </Tooltip>
                                    <Button
                                        type="text"
                                        danger
                                        icon={<MinusCircleOutlined />}
                                        onClick={() => handleRemoveMetric(metric.id)}
                                    />
                                </Space>
                            </Col>
                        </Row>
                        </div>
                    )}
                    </Draggable>
                ))}
                {provided.placeholder}
                </div>
            )}
            </Droppable>
        </DragDropContext>
      )}

      <Form.Item style={{ marginTop: 24 }}>
        <Button type="primary" onClick={handleMetricSettingsSubmit} loading={submitting} icon={<SaveOutlined />}>
          Metrik Ayarlarını Kaydet
        </Button>
      </Form.Item>
    </Card>
  );

  // Render the brand codes tab content
  const renderBrandCodesTab = () => (
    <Card title="Entegrasyon & API Kodları" bordered={false}>
      <Alert
          message="Marka Kodları"
          description="Bu bölümde markaya ait entegrasyon bilgilerini, API anahtarlarını ve diğer özel kodları yönetebilirsiniz. Örneğin Google Analytics ID, Facebook Pixel, API token'ları vb."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
      />
      <BrandCodes brandId={brandId} />
    </Card>
  );

  return (
    <Card>
      <Title level={3}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/brands')} style={{ marginRight: 16 }} />
        {isNewBrand ? 'Yeni Marka Oluştur' : `Marka Düzenle: ${brandData?.name || ''}`}
      </Title>
      <Divider />

      {isNewBrand ? (
        // Yeni marka oluşturmada sadece temel bilgiler göster
        renderBrandInfoTab()
      ) : (
        // Mevcut marka düzenlemede sekmeli yapı kullan
        <Tabs activeKey={activeTab} onChange={handleTabChange}>
          <TabPane 
            tab={<span><InfoCircleOutlined /> Marka Bilgileri</span>} 
            key="info"
          >
            {renderBrandInfoTab()}
          </TabPane>
          <TabPane 
            tab={<span><SettingOutlined /> Metrikler</span>} 
            key="metrics"
          >
            {renderMetricsTab()}
          </TabPane>
          <TabPane 
            tab={<span><CodeOutlined /> Marka Kodları</span>} 
            key="codes"
          >
            {renderBrandCodesTab()}
          </TabPane>
        </Tabs>
      )}
    </Card>
  );
};

export default BrandEditPage; 